import { Fragment, useEffect, useRef, useState } from 'react'
import {
    Controller,
    FormProvider,
    useFieldArray,
    useForm,
} from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod/v3'
import { Camera, Plus, ShoppingCart, Trash2, Truck, Users } from 'lucide-react'
import { toast } from 'sonner'
import type { OrderWorkflowTracker } from '@/lib/order-workflow-tracking'

import type { OrderItemDraft } from '@/components/forms/order-item-row'
import type { CreateOrderRequest } from '@/lib/api/sales-order-api'
import type { Customer } from '@/lib/api/customer-api'
import type { Product } from '@/lib/api/product-api'
import type { PackagingDraftItem } from '@/lib/packaging-draft'
import type { PickedPackagingMaterial } from '@/components/packaging/packaging-material-picker'
import type { InvoiceBuyerFormValue } from '@/components/forms/invoice-buyer-fields'
import type { MoneyLine } from '@/lib/order-money'
import {
    trackOrderField,
    trackOrderValidation,
} from '@/lib/order-workflow-field'
import { buildManualRow } from '@/lib/packaging-draft'
import { PackagingMaterialPicker } from '@/components/packaging/packaging-material-picker'
import { MobileMaterialCard } from '@/components/orders/mobile-material-card'
import { CodFreightFields } from '@/components/orders/cod-freight-fields'
import {
    DEFAULT_CREATE_INVOICE_BUYER,
    InvoiceBuyerFields,
    applyBuyerPatch,
    buildInvoiceBuyerPayload,
} from '@/components/forms/invoice-buyer-fields'
import { useFeatureFlag } from '@/hooks/use-feature-flag'
import { newMaterialRowId, rowKeyOf } from '@/lib/material-row-id'
import { buildLineSortOrders, mergeOrderLines } from '@/lib/order-line-order'
import { useOrderPackagingSync } from '@/hooks/use-order-packaging-sync'
import { FormSection } from '@/components/forms/form-section'
import { CustomerSearchSelect } from '@/components/forms/customer-search-select'
import { ProvinceWardPicker } from '@/components/forms/province-ward-picker'
import { ShippingCarrierSelect } from '@/components/forms/shipping-carrier-select'
import { MobileBarcodeScannerDialog } from '@/components/orders/mobile-barcode-scanner-dialog'
import { PosBarcodeNotFoundDialog } from '@/components/pos/pos-barcode-not-found-dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Field } from '@/components/ui/field'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { useConfirmOrder, useCreateOrder } from '@/hooks/use-sales-order-query'
import { ProductPicker } from '@/components/products/product-picker'
import { useInvoiceSettings } from '@/hooks/use-invoice-query'
import { formatVND } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'
import { posthog } from '@/lib/posthog'
import { orderCreatedProps } from '@/lib/posthog-order-event'
import {
    DEFAULT_SHIPPING_CARRIER_ID,
    WALKIN_CUSTOMER,
    WALKIN_CUSTOMER_ID,
} from '@/lib/constants'
import { vatFromGross } from '@/lib/order-money'

function getSourceChannels() {
    return [
        { value: 'direct', label: m.order_channel_direct() },
        { value: 'phone', label: m.order_channel_phone() },
        { value: 'online', label: m.order_channel_online() },
        { value: 'agent', label: m.order_channel_agent() },
        { value: 'other', label: m.order_channel_other() },
    ]
}

const EMPTY_ITEM: OrderItemDraft = {
    product_id: '',
    product_name: '',
    quantity: 1,
    unit_price: 0,
    discount_percent: 0,
    row_seq: 0,
}

interface Props {
    workflow?: OrderWorkflowTracker
    defaultCustomerId?: string
    onSuccess?: () => void
    onCancel?: () => void
}

export function MobileSalesOrderForm({
    defaultCustomerId,
    onSuccess,
    onCancel,
    workflow,
}: Props) {
    const orderItemSchema = z.object({
        product_id: z.string().min(1),
        product_name: z.string(),
        quantity: z.coerce.number().int().min(1),
        unit_price: z.coerce.number().min(0),
        discount_percent: z.coerce.number().min(0).max(100),
        // Số thứ tự dòng trên danh sách hàng hóa — chỉ để sắp xếp ở client,
        // gửi lên API dưới dạng sort_order (xem @/lib/order-line-order).
        row_seq: z.coerce.number().optional(),
    })

    const schema = z.object({
        source_channel: z.string().min(1),
        customer_phone: z
            .string()
            .min(1, m.validation_required_phone())
            .min(8, m.validation_phone_min()),
        shipping_address: z.string().min(1, m.validation_required_address()),
        shipping_province: z.string().optional(),
        shipping_province_code: z
            .string()
            .min(1, m.validation_required_province()),
        shipping_ward: z.string().optional(),
        shipping_ward_code: z.string().min(1, m.validation_required_ward()),
        shipping_carrier_id: z.string().min(1, m.validation_required()),
        shipping_fee_payer: z.enum(['customer', 'company']).optional(),
        product_notes: z.string().optional(),
        collect_cod: z.boolean().optional(),
        items: z.array(orderItemSchema).min(1),
    })

    type FormData = z.infer<typeof schema>

    const createMutation = useCreateOrder()
    const confirmMutation = useConfirmOrder()
    const { data: invoiceSettings } = useInvoiceSettings()
    // VAT chỉnh được (2 chiều % ↔ tiền). Mặc định từ cài đặt, cho ghi đè. Quản lý
    // ngoài RHF. vatAmountOverride = số tiền gõ tay; null = suy từ %.
    const [vatPercent, setVatPercent] = useState(0)
    const [vatAmountOverride, setVatAmountOverride] = useState<number | null>(
        null,
    )
    const vatTouched = useRef(false)
    useEffect(() => {
        if (!vatTouched.current && invoiceSettings?.vat_percent != null) {
            setVatPercent(invoiceSettings.vat_percent)
        }
    }, [invoiceSettings?.vat_percent])

    const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(
        null,
    )
    const [scannerOpen, setScannerOpen] = useState(false)
    const [notFoundBarcode, setNotFoundBarcode] = useState('')
    const [notFoundOpen, setNotFoundOpen] = useState(false)
    // Người mua công ty (HĐĐT) — ẩn sau flag dev-einvoice tới khi ký hợp đồng MISA.
    const einvoiceEnabled = useFeatureFlag('dev-einvoice')
    const [invoiceBuyer, setInvoiceBuyer] = useState<InvoiceBuyerFormValue>(
        DEFAULT_CREATE_INVOICE_BUYER,
    )
    // Phase 4 — vật tư bao bì sale chốt (autofill từ BOM, có thể edit).
    const [packagingItems, setPackagingItems] = useState<PackagingDraftItem[]>(
        [],
    )

    const form = useForm<FormData>({
        resolver: zodResolver(schema),
        defaultValues: {
            source_channel: 'direct',
            customer_phone: '',
            shipping_address: '',
            shipping_province: '',
            shipping_province_code: '',
            shipping_ward: '',
            shipping_ward_code: '',
            shipping_carrier_id: DEFAULT_SHIPPING_CARRIER_ID,
            // "Thu cước" unset by default — staff ticks gửi/nhận trả cước.
            shipping_fee_payer: undefined,
            product_notes: '',
            collect_cod: true,
            items: [{ ...EMPTY_ITEM }],
        },
    })

    const { fields, append, remove } = useFieldArray({
        control: form.control,
        name: 'items',
    })

    // Bộ đếm dòng dùng CHUNG cho sản phẩm và vật tư: dòng thêm sau luôn nằm
    // cuối danh sách, kể cả khi xen kẽ hai loại (FD #358). Dòng sản phẩm mặc
    // định của form đã mang row_seq = 0.
    const rowSeqRef = useRef(1)
    const nextRowSeq = () => rowSeqRef.current++

    const handleCustomerChange = (customer: Customer | null) => {
        setSelectedCustomer(customer)
        form.setValue('customer_phone', customer?.phone ?? '', {
            shouldValidate: false,
        })
        if (customer?.address) {
            form.setValue('shipping_address', customer.address)
        }
        // Prefill the shipping destination from the customer's saved admin units.
        form.setValue('shipping_province', customer?.province ?? '')
        form.setValue('shipping_province_code', customer?.province_code ?? '')
        form.setValue('shipping_ward', customer?.ward ?? '')
        form.setValue('shipping_ward_code', customer?.ward_code ?? '')
        // Dùng setValue theo path thay vì useFieldArray.update() để field.id
        // ổn định — field.id là line_key nối vật tư theo định mức với dòng SP,
        // update() remount dòng làm đứt liên kết và mất focus input đang gõ.
        const groupDiscount = customer?.customer_group?.discount_percent ?? 0
        const currentItems = form.getValues('items')
        currentItems.forEach((_, idx) => {
            form.setValue(`items.${idx}.discount_percent`, groupDiscount, {
                shouldDirty: true,
            })
        })
    }

    // Gán dữ liệu sản phẩm vào 1 dòng bằng setValue theo path (giữ field.id).
    const setItemProduct = (
        index: number,
        product: Product,
        qty: number,
        groupDiscount: number,
    ) => {
        form.setValue(`items.${index}.product_id`, product.id, {
            shouldDirty: true,
        })
        form.setValue(`items.${index}.product_name`, product.name)
        form.setValue(`items.${index}.quantity`, qty)
        form.setValue(`items.${index}.unit_price`, product.unit_price)
        form.setValue(`items.${index}.discount_percent`, groupDiscount)
    }

    const addProductToItems = (product: Product, qty = 1) => {
        const groupDiscount =
            selectedCustomer?.customer_group?.discount_percent ?? 0
        // Check if product already in list — increment qty instead
        const currentItems = form.getValues('items')
        const existing = currentItems.findIndex(
            (item) => item.product_id === product.id,
        )
        if (existing >= 0) {
            const item = currentItems[existing]
            form.setValue(
                `items.${existing}.quantity`,
                (item.quantity ?? 0) + qty,
                { shouldDirty: true },
            )
        } else {
            // Replace first empty slot or append
            const emptyIdx = currentItems.findIndex((item) => !item.product_id)
            if (emptyIdx >= 0) {
                setItemProduct(emptyIdx, product, qty, groupDiscount)
            } else {
                append({
                    product_id: product.id,
                    product_name: product.name,
                    quantity: qty,
                    unit_price: product.unit_price,
                    discount_percent: groupDiscount,
                    row_seq: nextRowSeq(),
                })
            }
        }
    }

    const handleProductCreated = (product: Product) => {
        addProductToItems(product)
    }

    const updateItem = (
        index: number,
        field: keyof OrderItemDraft,
        value: string | number,
    ) => {
        // setValue theo path (không update()) để field.id ổn định — xem chú
        // thích ở handleCustomerChange. Path dựng động nên phải cast: field là
        // keyof OrderItemDraft (rộng hơn schema mobile), value union string|number.
        form.setValue(
            `items.${index}.${field}` as `items.${number}.quantity`,
            value as never,
            { shouldDirty: true },
        )
    }

    const watchedItems = form.watch('items')
    const selectedLineCount =
        watchedItems.filter((item) => Boolean(item.product_id)).length +
        packagingItems.filter((item) => Boolean(item.packaging_material_id))
            .length
    useEffect(() => {
        workflow?.setContext({ lineCount: selectedLineCount })
    }, [workflow, selectedLineCount])

    // Vật tư của đơn giờ CHỈ do nhân viên tự thêm (#M-16) — đơn không tự sinh
    // theo định mức nữa, việc đó chuyển sang phiếu nhập kho hàng bán. Hook chỉ
    // còn bù số tồn cho các dòng thêm tay.
    const { availableByMaterial } = useOrderPackagingSync({
        value: packagingItems,
        onChange: setPackagingItems,
    })

    // Vật tư thêm tay = dòng không gắn dòng đơn nào. Vật tư theo định mức có
    // line_key và render dưới đúng sản phẩm của chúng.
    const manualMaterialRows = packagingItems.filter((p) => !p.line_key)

    // Thứ tự hiển thị: card sản phẩm và card vật tư thêm tay trộn chung theo
    // row_seq nên hai loại xen kẽ được (FD #358). Vật tư theo định mức không
    // nằm ở đây — chúng luôn render ngay dưới card sản phẩm của mình.
    const displayRows = mergeOrderLines(
        fields.map((f) => f.row_seq),
        manualMaterialRows.map((mat) => mat.row_seq),
    )

    // Mọi callback khớp theo KHOÁ DÒNG (rowKeyOf) — dùng chung cho cả vật tư theo
    // định mức lẫn thêm tay vì rowKeyOf duy nhất cho từng dòng.
    const patchMaterialRow = (
        rowKey: string,
        patch: (p: PackagingDraftItem) => PackagingDraftItem,
    ) =>
        setPackagingItems((prev) =>
            prev.map((p) => (rowKeyOf(p) === rowKey ? patch(p) : p)),
        )

    const handleMaterialRowQty = (rowKey: string, v: string) =>
        patchMaterialRow(rowKey, (p) => ({
            ...p,
            quantity: Math.max(0, Number(v) || 0),
        }))

    const handleMaterialRowPrice = (rowKey: string, v: string) =>
        patchMaterialRow(rowKey, (p) => ({
            ...p,
            unit_price: Math.max(0, Number(v) || 0),
        }))

    const handleMaterialRowDiscount = (rowKey: string, v: string) =>
        patchMaterialRow(rowKey, (p) => ({
            ...p,
            discount_percent: Math.min(100, Math.max(0, Number(v) || 0)),
        }))

    const handleMaterialRowRemove = (rowKey: string) =>
        setPackagingItems((prev) => prev.filter((p) => rowKeyOf(p) !== rowKey))

    const handleAddMaterialRow = (mat: PickedPackagingMaterial) =>
        setPackagingItems((prev) => [
            ...prev,
            {
                ...buildManualRow(
                    prev,
                    mat,
                    availableByMaterial,
                    newMaterialRowId(),
                ),
                row_seq: nextRowSeq(),
            },
        ])

    // Xoá dòng SP: bỏ luôn vật tư theo định mức gắn dòng đó (line_key = field.id)
    // để không còn vật tư "mồ côi" tính tiền/gửi payload trước khi hook refetch.
    const handleRemoveItem = (index: number) => {
        const removedFieldId = fields[index]?.id
        remove(index)
        if (removedFieldId) {
            setPackagingItems((prev) =>
                prev.filter((p) => p.line_key !== removedFieldId),
            )
        }
    }

    const itemSubtotal = watchedItems.reduce(
        (sum, item) =>
            sum +
            (item.quantity ?? 0) *
                (item.unit_price ?? 0) *
                (1 - (item.discount_percent ?? 0) / 100),
        0,
    )
    // Tiền vật tư (giá vật tư) — chịu chiết khấu dòng như dòng hàng, cộng vào
    // base cùng SP trước VAT.
    const materialTotal = packagingItems.reduce(
        (sum, p) =>
            sum +
            (p.unit_price ?? 0) *
                (p.quantity ?? 0) *
                (1 - (p.discount_percent ?? 0) / 100),
        0,
    )
    const baseAmount = itemSubtotal + materialTotal
    const roundedBase = Math.round(baseAmount)
    // M-78: VAT mặc định = tổng theo giá gồm VAT từng đơn vị − tiền hàng, để
    // tổng tròn theo giá niêm yết (giá chưa VAT thường lẻ: 100.000 / 1,08).
    const moneyLines: MoneyLine[] = [
        ...watchedItems.map((item) => ({
            quantity: item.quantity ?? 0,
            unitPrice: item.unit_price ?? 0,
            discountPercent: item.discount_percent ?? 0,
        })),
        ...packagingItems.map((p) => ({
            quantity: p.quantity ?? 0,
            unitPrice: p.unit_price ?? 0,
            discountPercent: p.discount_percent ?? 0,
        })),
    ]
    // VAT làm tròn về số nguyên (VND không có subunit). Override = tiền gõ tay.
    const vatAmount =
        vatAmountOverride != null
            ? vatAmountOverride
            : vatPercent > 0
              ? vatFromGross(moneyLines, 0, vatPercent, roundedBase)
              : 0
    const totalAmount = roundedBase + vatAmount

    useEffect(() => {
        setVatAmountOverride(null)
    }, [roundedBase])

    const handleVatPercentChange = (v: number) => {
        vatTouched.current = true
        setVatAmountOverride(null)
        setVatPercent(Number.isFinite(v) && v >= 0 ? v : 0)
    }
    const handleVatAmountChange = (amt: number) => {
        vatTouched.current = true
        const safe = Number.isFinite(amt) && amt >= 0 ? Math.round(amt) : 0
        setVatAmountOverride(safe)
        setVatPercent(
            roundedBase > 0
                ? Math.round((safe / roundedBase) * 100 * 100) / 100
                : 0,
        )
    }

    const buildPayload = (data: FormData): CreateOrderRequest => {
        // Walk-in fallback when no customer is picked.
        const buyer = selectedCustomer ?? WALKIN_CUSTOMER
        // field.id (line_key) → index dòng đơn; data.items cùng thứ tự với fields.
        const lineIndexByKey = new Map(fields.map((f, i) => [f.id, i]))
        // Thứ tự người bán đã nhập (sản phẩm xen kẽ vật tư) đánh lại thành
        // sort_order 0..n-1 để BE lưu và mọi màn đọc lại đúng thứ tự.
        const payloadMaterials = packagingItems.filter((p) => p.quantity > 0)
        const { itemSortOrders, materialSortOrders } = buildLineSortOrders({
            items: fields.map((f) => ({ key: f.id, row_seq: f.row_seq })),
            materials: payloadMaterials.map((p) => ({
                line_key: p.line_key,
                row_seq: p.row_seq,
            })),
        })
        return {
            source_channel: data.source_channel,
            customer_id: buyer.id,
            customer_name: buyer.name,
            customer_phone: data.customer_phone.trim(),
            shipping_address: data.shipping_address,
            shipping_province: data.shipping_province || undefined,
            shipping_province_code: data.shipping_province_code || undefined,
            shipping_ward: data.shipping_ward || undefined,
            shipping_ward_code: data.shipping_ward_code || undefined,
            shipping_carrier_id:
                data.shipping_carrier_id || DEFAULT_SHIPPING_CARRIER_ID,
            // Freight amount is no longer entered here — the depot settles it
            // later. Only the "Thu cước" intent (payer) is recorded.
            shipping_fee: undefined,
            shipping_fee_payer: data.shipping_fee_payer,
            product_notes: data.product_notes || undefined,
            collect_cod: data.collect_cod ?? null,
            // Cổng "Xuất hóa đơn điện tử": tick → gửi MISA khi giao thành công.
            issue_einvoice: einvoiceEnabled ? invoiceBuyer.enabled : false,
            // Người mua công ty (HĐĐT): chỉ gửi khi bật e-invoice + tick.
            invoice_buyer: einvoiceEnabled
                ? buildInvoiceBuyerPayload(invoiceBuyer, 'create')
                : undefined,
            vat_percent: vatPercent,
            vat_amount: vatAmount,
            items: data.items.map((item, idx) => ({
                product_id: item.product_id,
                quantity: item.quantity,
                unit_price: item.unit_price,
                discount_percent: item.discount_percent || undefined,
                sort_order: itemSortOrders[idx],
            })),
            packaging_items: payloadMaterials.map((p, idx) => ({
                packaging_material_id: p.packaging_material_id,
                quantity: p.quantity,
                source: p.source,
                unit_price: p.unit_price,
                discount_percent: p.discount_percent,
                // Rỗng cho vật tư thêm tay → OMIT (BE bind uuid.UUID, chuỗi
                // '' gây "Invalid request body").
                product_id: p.product_id || undefined,
                // Vật tư gắn dòng đơn (line_key = field.id) → index dòng để
                // BE lưu order_item_id.
                order_item_index: p.line_key
                    ? lineIndexByKey.get(p.line_key)
                    : undefined,
                sort_order: materialSortOrders[idx],
            })),
        }
    }

    /** Save only — creates order in "new" status */
    const onInvalid = (errors: object) => trackOrderValidation(errors, workflow)

    const handleSave = form.handleSubmit(async (data) => {
        try {
            const payload = buildPayload(data)
            workflow?.setContext({ lineCount: selectedLineCount })
            const endWait = workflow?.submit()
            let response
            try {
                response = await createMutation.mutateAsync(payload)
            } catch (error) {
                workflow?.step('api_failed')
                throw error
            } finally {
                endWait?.()
            }
            if (response.data?.id) {
                workflow?.created(response.data.id)
                workflow?.finish('api_confirmed', response.data.id)
            }
            // Reference point for the analytics drift check, not a sales
            // count — see the policy note in @/lib/posthog.
            try {
                posthog.capture(
                    'sales_order_created',
                    orderCreatedProps({
                        orderId: response.data?.id,
                        sourceChannel: data.source_channel,
                        itemCount: data.items.length,
                        totalAmount,
                        customerId: payload.customer_id,
                        walkInCustomerId: WALKIN_CUSTOMER_ID,
                        extra: {
                            via: 'mobile',
                            ...(workflow
                                ? { attempt_id: workflow.attemptId }
                                : {}),
                        },
                    }),
                )
            } catch {
                /* Analytics must not interrupt saving. */
            }
            onSuccess?.()
        } catch {
            // errors handled via toast in mutation
        }
    }, onInvalid)

    /** Save & Confirm — creates then immediately confirms */
    const handleSaveAndConfirm = form.handleSubmit(async (data) => {
        try {
            const payload = buildPayload(data)
            workflow?.setContext({ lineCount: selectedLineCount })
            const endWait = workflow?.submit()
            let response
            try {
                response = await createMutation.mutateAsync(payload)
            } catch (error) {
                workflow?.step('api_failed')
                throw error
            } finally {
                endWait?.()
            }
            if (response.data?.id) {
                workflow?.created(response.data.id)
                workflow?.finish('api_confirmed', response.data.id)
            }
            const orderId = response.data?.id
            // Reference point for the analytics drift check, not a sales
            // count — see the policy note in @/lib/posthog.
            try {
                posthog.capture(
                    'sales_order_created',
                    orderCreatedProps({
                        orderId,
                        sourceChannel: data.source_channel,
                        itemCount: data.items.length,
                        totalAmount,
                        customerId: payload.customer_id,
                        walkInCustomerId: WALKIN_CUSTOMER_ID,
                        extra: {
                            via: 'mobile_confirm',
                            ...(workflow
                                ? { attempt_id: workflow.attemptId }
                                : {}),
                        },
                    }),
                )
            } catch {
                /* Analytics must not interrupt confirmation. */
            }
            if (orderId) {
                workflow?.step('confirm_requested')
                try {
                    await confirmMutation.mutateAsync({ id: orderId })
                } catch (error) {
                    workflow?.step('confirm_failed')
                    throw error
                }
            } else {
                toast.error(m.common_save_error())
            }
            onSuccess?.()
        } catch {
            // errors handled via toast in mutation
        }
    }, onInvalid)

    const isPending = createMutation.isPending || confirmMutation.isPending

    return (
        <FormProvider {...form}>
            {/* Camera scanner dialog */}
            <MobileBarcodeScannerDialog
                open={scannerOpen}
                onOpenChange={setScannerOpen}
                onProductFound={(product) => {
                    workflow?.setContext({ entryMethod: 'barcode' })
                    workflow?.step('product_selected', {
                        entryMethod: 'barcode',
                    })
                    addProductToItems(product)
                }}
                onBarcodeNotFound={(barcode) => {
                    setNotFoundBarcode(barcode)
                    setNotFoundOpen(true)
                }}
            />

            {/* Barcode not-found dialog */}
            <PosBarcodeNotFoundDialog
                open={notFoundOpen}
                onOpenChange={setNotFoundOpen}
                barcode={notFoundBarcode}
                onProductCreated={handleProductCreated}
            />

            <form
                className="pb-28"
                onFocusCapture={(e) => trackOrderField(e.target, workflow)}
                onClickCapture={(e) => trackOrderField(e.target, workflow)}
                onInputCapture={(e) => trackOrderField(e.target, workflow)}
            >
                <div className="space-y-4 px-4 pt-4">
                    <FormSection
                        icon={<Users />}
                        data-order-group="customer"
                        title={m.order_customer()}
                    >
                        {/* Customer */}
                        <div className="space-y-1">
                            <label className="text-sm font-medium">
                                {m.order_customer()}
                            </label>
                            <CustomerSearchSelect
                                value={selectedCustomer}
                                onChange={handleCustomerChange}
                                defaultCustomerId={defaultCustomerId}
                                workflow={workflow}
                            />
                            <p className="text-xs text-muted-foreground">
                                {m.order_customer_walkin_hint()}
                            </p>
                        </div>

                        {/* Channel */}
                        <Field
                            name="source_channel"
                            label={m.customer_sales_channel()}
                            required
                        >
                            <Controller
                                name="source_channel"
                                control={form.control}
                                render={({ field }) => (
                                    <Select
                                        value={field.value}
                                        onValueChange={field.onChange}
                                    >
                                        <SelectTrigger className="min-h-11 text-base">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {getSourceChannels().map((c) => (
                                                <SelectItem
                                                    key={c.value}
                                                    value={c.value}
                                                >
                                                    {c.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                )}
                            />
                        </Field>

                        {/* Phone */}
                        <Field
                            name="customer_phone"
                            label={m.customer_phone_label()}
                            required
                        >
                            <Input
                                id="customer_phone"
                                type="tel"
                                inputMode="tel"
                                autoComplete="tel"
                                className="min-h-11 text-base"
                                {...form.register('customer_phone')}
                            />
                        </Field>
                    </FormSection>

                    <FormSection
                        icon={<Truck />}
                        data-order-group="shipping"
                        title={m.order_section_delivery()}
                    >
                        {/* Address */}
                        <Field
                            name="shipping_address"
                            label={m.customer_shipping_address()}
                            required
                        >
                            <Input
                                id="shipping_address"
                                className="min-h-11 text-base"
                                {...form.register('shipping_address')}
                            />
                        </Field>

                        <ProvinceWardPicker
                            value={{
                                provinceCode:
                                    form.watch('shipping_province_code') ||
                                    undefined,
                                provinceName: form.watch('shipping_province'),
                                wardCode:
                                    form.watch('shipping_ward_code') ||
                                    undefined,
                                wardName: form.watch('shipping_ward'),
                            }}
                            onChange={(next) => {
                                form.setValue(
                                    'shipping_province_code',
                                    next.provinceCode ?? '',
                                    { shouldValidate: true },
                                )
                                form.setValue(
                                    'shipping_province',
                                    next.provinceName ?? '',
                                )
                                form.setValue(
                                    'shipping_ward_code',
                                    next.wardCode ?? '',
                                    { shouldValidate: true },
                                )
                                form.setValue(
                                    'shipping_ward',
                                    next.wardName ?? '',
                                )
                            }}
                        />
                        {(form.formState.errors.shipping_province_code ||
                            form.formState.errors.shipping_ward_code) && (
                            <p className="text-sm text-destructive">
                                {form.formState.errors.shipping_province_code
                                    ?.message ??
                                    form.formState.errors.shipping_ward_code
                                        ?.message}
                            </p>
                        )}

                        <Field
                            name="shipping_carrier_id"
                            label={m.order_form_carrier_label()}
                        >
                            <Controller
                                name="shipping_carrier_id"
                                control={form.control}
                                render={({ field }) => (
                                    <ShippingCarrierSelect
                                        id="shipping_carrier_id"
                                        value={field.value}
                                        onValueChange={field.onChange}
                                        className="min-h-11 text-base"
                                    />
                                )}
                            />
                        </Field>

                        {/* Thu tiền (COD) + Thu cước vận chuyển — dùng chung
                            component với desktop / màn sửa đơn. */}
                        <div className="contents" data-order-group="payment">
                            <CodFreightFields
                                idPrefix="order-mobile"
                                collectCod={form.watch('collect_cod')}
                                onCollectCodChange={(v) =>
                                    form.setValue('collect_cod', v, {
                                        shouldValidate: true,
                                    })
                                }
                                feePayer={form.watch('shipping_fee_payer')}
                                onFeePayerChange={(p) =>
                                    form.setValue('shipping_fee_payer', p, {
                                        shouldValidate: true,
                                    })
                                }
                            />
                        </div>

                        {/* Ghi chú — gộp vào cuối phần giao hàng. */}
                        <Field
                            name="product_notes"
                            label={m.customer_product_notes()}
                        >
                            <Input
                                id="product_notes"
                                className="min-h-11 text-base"
                                {...form.register('product_notes')}
                            />
                        </Field>
                    </FormSection>

                    <FormSection
                        icon={<ShoppingCart />}
                        data-order-group="items"
                        title={m.order_products()}
                    >
                        {/* Product cards */}
                        <div className="space-y-2">
                            {displayRows.map((row) => {
                                if (row.kind === 'material') {
                                    const mat = manualMaterialRows[row.index]
                                    return (
                                        <MobileMaterialCard
                                            key={rowKeyOf(mat)}
                                            row={mat}
                                            standalone
                                            onQtyChange={handleMaterialRowQty}
                                            onPriceChange={
                                                handleMaterialRowPrice
                                            }
                                            onDiscountChange={
                                                handleMaterialRowDiscount
                                            }
                                            onRemove={handleMaterialRowRemove}
                                        />
                                    )
                                }
                                const idx = row.index
                                const field = fields[idx]
                                const item = watchedItems[idx]
                                const lineTotal =
                                    (item?.quantity ?? 0) *
                                    (item?.unit_price ?? 0) *
                                    (1 - (item?.discount_percent ?? 0) / 100)
                                // Vật tư theo định mức gắn dòng SP này (line_key
                                // = field.id) — render ngay dưới card sản phẩm.
                                const materialRows = packagingItems.filter(
                                    (p) => p.line_key === field.id,
                                )

                                return (
                                    <Fragment key={field.id}>
                                        <div className="rounded-md border bg-card p-3 space-y-2">
                                            {/* Product select */}
                                            <ProductPicker
                                                workflow={workflow}
                                                value={item?.product_id || ''}
                                                onChange={(
                                                    productId,
                                                    product,
                                                ) => {
                                                    if (!product) return
                                                    updateItem(
                                                        idx,
                                                        'product_id',
                                                        productId,
                                                    )
                                                    updateItem(
                                                        idx,
                                                        'product_name',
                                                        product.name,
                                                    )
                                                    updateItem(
                                                        idx,
                                                        'unit_price',
                                                        product.unit_price,
                                                    )
                                                }}
                                                placeholder={m.order_select_product_placeholder()}
                                                className="min-h-11 text-base"
                                            />

                                            {/* Bottom row: qty + price + discount + subtotal + remove */}
                                            <div className="flex items-center gap-2">
                                                <div className="flex flex-col gap-0.5 flex-1">
                                                    <span className="text-xs text-muted-foreground">
                                                        {m.order_quantity()}
                                                    </span>
                                                    <Input
                                                        type="number"
                                                        min={1}
                                                        inputMode="numeric"
                                                        value={
                                                            item?.quantity ?? 1
                                                        }
                                                        onChange={(e) =>
                                                            updateItem(
                                                                idx,
                                                                'quantity',
                                                                Number(
                                                                    e.target
                                                                        .value,
                                                                ),
                                                            )
                                                        }
                                                        className="min-h-10 text-base w-full"
                                                    />
                                                </div>
                                                <div className="flex flex-col gap-0.5 flex-[2]">
                                                    <span className="text-xs text-muted-foreground">
                                                        {m.order_unit_price()}
                                                    </span>
                                                    <Input
                                                        type="number"
                                                        min={0}
                                                        // step="any" so fractional đồng are
                                                        // accepted (an explicit step blocks
                                                        // anything not a multiple of it, and the
                                                        // default 1 blocks decimals outright).
                                                        step="any"
                                                        inputMode="decimal"
                                                        value={
                                                            item?.unit_price ??
                                                            0
                                                        }
                                                        onChange={(e) =>
                                                            updateItem(
                                                                idx,
                                                                'unit_price',
                                                                Number(
                                                                    e.target
                                                                        .value,
                                                                ),
                                                            )
                                                        }
                                                        className="min-h-10 text-base w-full"
                                                    />
                                                </div>
                                                <div className="flex flex-col gap-0.5 w-16">
                                                    <span className="text-xs text-muted-foreground">
                                                        {m.order_discount_short()}
                                                    </span>
                                                    <Input
                                                        type="number"
                                                        min={0}
                                                        max={100}
                                                        inputMode="numeric"
                                                        value={
                                                            item?.discount_percent ??
                                                            0
                                                        }
                                                        onChange={(e) =>
                                                            updateItem(
                                                                idx,
                                                                'discount_percent',
                                                                Number(
                                                                    e.target
                                                                        .value,
                                                                ),
                                                            )
                                                        }
                                                        className="min-h-10 text-base w-full"
                                                    />
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        handleRemoveItem(idx)
                                                    }
                                                    className="mt-4 shrink-0 p-2 text-destructive min-h-10 min-w-10 flex items-center justify-center"
                                                >
                                                    <Trash2 className="size-4" />
                                                </button>
                                            </div>

                                            {/* Line subtotal */}
                                            <div className="text-right text-sm font-medium text-muted-foreground">
                                                {new Intl.NumberFormat(
                                                    'vi-VN',
                                                ).format(lineTotal)}{' '}
                                                ₫
                                            </div>
                                        </div>
                                        {/* Vật tư theo định mức của dòng này */}
                                        {materialRows.map((mat) => (
                                            <MobileMaterialCard
                                                key={rowKeyOf(mat)}
                                                row={mat}
                                                onQtyChange={
                                                    handleMaterialRowQty
                                                }
                                                onPriceChange={
                                                    handleMaterialRowPrice
                                                }
                                                onDiscountChange={
                                                    handleMaterialRowDiscount
                                                }
                                                onRemove={
                                                    handleMaterialRowRemove
                                                }
                                            />
                                        ))}
                                    </Fragment>
                                )
                            })}
                        </div>

                        {/* Add product buttons */}
                        <div className="flex gap-2">
                            <Button
                                type="button"
                                variant="outline"
                                className="flex-1 min-h-11 gap-1"
                                onClick={() => setScannerOpen(true)}
                            >
                                <Camera className="size-4" />
                                {m.mobile_scan_barcode_button()}
                            </Button>
                            <Button
                                type="button"
                                variant="outline"
                                className="flex-1 min-h-11 gap-1"
                                onClick={() =>
                                    append({
                                        ...EMPTY_ITEM,
                                        discount_percent:
                                            selectedCustomer?.customer_group
                                                ?.discount_percent ?? 0,
                                        row_seq: nextRowSeq(),
                                    })
                                }
                            >
                                <Plus className="size-4" />
                                {m.common_add_row()}
                            </Button>
                        </div>

                        {/* Thêm vật tư có giá vào đơn. */}
                        <PackagingMaterialPicker
                            onPick={handleAddMaterialRow}
                            placeholder={m.packaging_add_material()}
                        />
                    </FormSection>

                    {/* Người mua công ty (HĐĐT) — dưới phần Sản phẩm; chỉ hiện
                        khi bật e-invoice. */}
                    {einvoiceEnabled && (
                        <InvoiceBuyerFields
                            idPrefix="order-mobile"
                            value={invoiceBuyer}
                            onChange={(patch) =>
                                setInvoiceBuyer((prev) =>
                                    applyBuyerPatch(
                                        prev,
                                        patch,
                                        selectedCustomer,
                                    ),
                                )
                            }
                        />
                    )}
                </div>
            </form>

            {/* Sticky bottom action bar — mobile only */}
            <div className="fixed bottom-0 left-0 right-0 z-20 border-t bg-background px-4 py-3 safe-area-pb">
                <div className="space-y-0.5 mb-2 text-sm">
                    <div className="flex items-center justify-between text-muted-foreground">
                        <span>{m.order_items_subtotal()}</span>
                        <span>{formatVND(itemSubtotal)}</span>
                    </div>
                    {materialTotal > 0 && (
                        <div className="flex items-center justify-between text-muted-foreground">
                            <span>{m.order_material_total()}</span>
                            <span>{formatVND(materialTotal)}</span>
                        </div>
                    )}
                    <div className="flex items-center justify-between gap-2 text-muted-foreground">
                        <span className="inline-flex items-center gap-1.5">
                            {m.order_vat_field_label()}
                            <input
                                type="number"
                                min={0}
                                max={100}
                                step="0.1"
                                inputMode="decimal"
                                aria-label={m.order_vat_field_label()}
                                value={vatPercent}
                                onChange={(e) =>
                                    handleVatPercentChange(
                                        e.target.valueAsNumber,
                                    )
                                }
                                className="focus-visible:border-ring w-14 rounded-md border bg-transparent px-2 py-1 text-right text-sm outline-none"
                            />
                            %
                        </span>
                        <input
                            type="number"
                            min={0}
                            step="1"
                            inputMode="numeric"
                            aria-label={m.order_vat_amount_label()}
                            value={Math.round(vatAmount)}
                            onChange={(e) =>
                                handleVatAmountChange(e.target.valueAsNumber)
                            }
                            className="focus-visible:border-ring w-28 rounded-md border bg-transparent px-2 py-1 text-right text-sm outline-none"
                        />
                    </div>
                    <div className="mt-1 flex items-center justify-between border-t pt-2">
                        <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                            {m.mobile_total_label()}
                        </span>
                        <span className="text-xl font-bold tabular-nums text-primary">
                            {formatVND(totalAmount)}
                        </span>
                    </div>
                </div>
                <div className="flex gap-2">
                    {onCancel && (
                        <Button
                            type="button"
                            variant="outline"
                            className="min-h-11"
                            onClick={onCancel}
                        >
                            {m.mobile_back_button()}
                        </Button>
                    )}
                    <Button
                        type="button"
                        variant="outline"
                        className="flex-1 min-h-11"
                        disabled={isPending}
                        onClick={() => void handleSave()}
                    >
                        {createMutation.isPending && !confirmMutation.isPending
                            ? m.common_saving()
                            : m.common_save()}
                    </Button>
                    <Button
                        type="button"
                        className="flex-1 min-h-11"
                        disabled={isPending}
                        onClick={() => void handleSaveAndConfirm()}
                    >
                        {isPending
                            ? m.common_processing()
                            : m.mobile_save_and_confirm()}
                    </Button>
                </div>
            </div>
        </FormProvider>
    )
}
